import React, { useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { BodySmallMidEmphasis } from '@/design-system';
import { css, cx } from '@emotion/css';
import colors from '@/theme/colors';
import { groupBy } from 'lodash';
import { CTAMapper } from '@/pages/FlyerDetails/CtaMapper';
import { CallToAction, CallToActionEnum } from '@/graphql/types';
import { pxToRem } from '@/utils';
import { AccessibleDivButton, AccessibleList, AccessibleListItem } from '@/components';

type Props = {
  ctas: CallToAction[];
  trackId: string;
  openLink: ({
    origin,
    value,
    cta,
    featureId,
  }: {
    origin: string;
    value: string;
    cta: string;
    featureId?: string | null;
  }) => void;
};

export interface FeatureLinkCardsRef {
  focusFirstCard: () => void;
}

const FeatureLinkCards = forwardRef<FeatureLinkCardsRef, Props>(
  ({ ctas, trackId, openLink }, ref) => {
    const firstCardRef = useRef<HTMLDivElement>(null);

    if (ctas.length == 0) return null;

    useImperativeHandle(ref, () => ({
      focusFirstCard: () => {
        if (firstCardRef.current) {
          firstCardRef.current.focus();
        }
      },
    }));

    const sortedFeaturedLinks = useMemo(() => {
      return [...ctas].sort((a, b) => {
        const getPriority = type => {
          if (type === 'email') return 2;
          if (type === 'call') return 3;
          return 1;
        };

        const priorityA = getPriority(a.type);
        const priorityB = getPriority(b.type);

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        return a.value.localeCompare(b.value);
      });
    }, [ctas]);

    const onClick = (type: string, value: string, id?: string | null) => {
      openLink({
        origin: `${trackId}:cta:${type}:${value}`,
        value: value,
        cta: type,
        featureId: id,
      });
    };

    return (
      <AccessibleList
        className={cx(
          styles.container,
          sortedFeaturedLinks.length < 5 ? styles.containerLeftAligned : styles.fullLinks
        )}
      >
        {sortedFeaturedLinks.map(({ type, value, id }, index) => {
          const Icon = CTAMapper[type].icon;
          const label = CTAMapper[type].label;

          return (
            <AccessibleListItem key={`${type}-${value}`}>
              <AccessibleDivButton
                ref={index === 0 ? firstCardRef : undefined}
                className={styles.card}
                onClick={() => onClick(type, value, id)}
                role="link"
                tabIndex={2}
              >
                <div className={styles.icon}>
                  <Icon />
                </div>
                <div>
                  <BodySmallMidEmphasis as="span">{label}</BodySmallMidEmphasis>
                </div>
              </AccessibleDivButton>
            </AccessibleListItem>
          );
        })}
      </AccessibleList>
    );
  }
);

const styles = {
  container: css`
    display: flex;
    flex-direction: row;
    margin-bottom: 32px;
  `,
  fullLinks: css`
    justify-content: space-between;
  `,
  containerLeftAligned: css`
    justify-content: flex-start;
  `,
  card: css`
    display: flex;
    width: ${pxToRem(67)};
    margin: 0px 6px 0px 6px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    justify-content: flex-start;
    text-align: center;
    cursor: pointer;
    &:first-child {
      margin-left: 0;
    }
  `,
  icon: css`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.lightPrussian};
    padding: ${pxToRem(8)};
    margin-bottom: ${pxToRem(4)};
    border-radius: ${pxToRem(24)};
    svg {
      color: ${colors.prussian};
    }
  `,
};

export { FeatureLinkCards };
